/**
 * Client ↔ consultant messaging — the CLIENT (portal) surface.
 *
 * Backend: `app/api/v1/client_messages_portal.py`, mounted at `/client/messages`,
 * so every path here reads `/v1/client/messages…`. **No route takes a client id**
 * — the caller's client is resolved server-side from the JWT through
 * `app.auth.tenant_scoping.get_client_for_user`, which is what makes client A
 * unable to name client B. Never add a client id to a call in this file.
 *
 * Lives in `shared` rather than `packages/portal` for one reason: the header
 * entry point and the presence heartbeat both hang off `PortalLayout`, which is
 * a shared component. Same placement as `notificationApi` + `stores/notifications`
 * (the NotificationBell precedent). Only `@claimshield/portal` mounts
 * `PortalLayout`, so nothing else in the monorepo picks this up.
 *
 * ## The internal-note boundary
 *
 * Staff can leave internal notes **on the client's own thread**. A note reaching
 * a client is a privacy failure, not a display glitch, so the boundary is
 * defended at three independent layers:
 *
 *   1. server SQL — `client_messaging.client_visible_messages_query` filters
 *      `is_internal_note = false`;
 *   2. server serialization — `PortalMessageOut` has no `is_internal_note` field
 *      for one to serialize into;
 *   3. **this file** — {@link sanitizeClientMessages} drops any row that arrives
 *      carrying the field at all, and reports it.
 *
 * Layer 3 is not redundancy theatre. Layers 1 and 2 both live in one Python
 * module; a single bad edit there takes out both. The client is the party
 * harmed, so the client's own code refuses the row — and, because a silently
 * dropped row would hide a live leak, the drop is loud (`console.error` +
 * `leakedInternalNotes`, which the portal surfaces).
 */
import { ref, type Ref } from 'vue'

import { api } from './api'

/** Who wrote a message. The portal styles the two sides differently. */
export type MessageSenderSide = 'client' | 'staff'

/**
 * One message as the CLIENT sees it.
 *
 * Mirrors `PortalMessageOut` field-for-field. `is_internal_note` is
 * **deliberately absent** — it is not "optional here", it must never arrive.
 * The `never` below makes a component that tries to read one a type error.
 */
export interface ClientThreadMessage {
  id: string
  body: string
  sender_name: string
  sender_side: MessageSenderSide
  created_at: string
  read_at: string | null
  /** Never present. Declared so any read of it fails to compile. */
  is_internal_note?: never
}

/**
 * The honest response-expectation copy, authored server-side
 * (`client_messaging.RESPONSE_EXPECTATION_*`).
 *
 * It ships in the payload rather than living in a Vue template so the portal
 * thread, the ops thread and the offline email cannot drift into promising
 * different things — and so a 3-consultant team never has a typing-indicator
 * SLA typed into a component by someone who did not know that was the rule.
 * `after_hours` is computed in the office's own timezone, not the browser's.
 */
export interface ResponseExpectation {
  headline: string
  detail: string
  after_hours: boolean
  business_hours: string
}

export interface ClientThread {
  client_id: string
  client_name: string | null
  /** Null until the client's first message mints the thread. */
  thread_id: string | null
  messages: ClientThreadMessage[]
  unread_count: number
  response_expectation: ResponseExpectation
}

export interface ClientReadResult {
  marked_read: number
  unread_count: number
}

export interface ClientHeartbeatResult {
  last_seen_at: string
  unread_count: number
  /** The server's single definition of "online". Never hardcode a number. */
  online_window_minutes: number
}

/** Raised past the API boundary when the server sent something it must not. */
export interface InternalNoteLeak {
  count: number
  at: string
}

/**
 * The most recent internal-note leak this session saw, or null.
 *
 * A `ref`, not a module-scoped variable: the portal renders a notice off this,
 * and a plain variable is invisible to Vue's reactivity — the computed reading
 * it would evaluate once (null), cache, and never re-run, so the leak would be
 * filtered correctly and reported to nobody. The e2e suite caught exactly that.
 */
const lastLeak: Ref<InternalNoteLeak | null> = ref(null)

/** Read-only view for components; the setter is this module's alone. */
export function internalNoteLeak(): InternalNoteLeak | null {
  return lastLeak.value
}

/** The reactive handle, for a computed that must re-evaluate when a leak lands. */
export const internalNoteLeakRef = lastLeak as Readonly<Ref<InternalNoteLeak | null>>

/**
 * Drop any message that arrived carrying `is_internal_note`, loudly.
 *
 * The server's contract is that the field is absent from this payload
 * entirely — so its mere PRESENCE is the signal, whatever its value. A
 * `false` here would mean the ops serializer had been swapped in, and the next
 * regression past that point ships notes with `true`.
 */
export function sanitizeClientMessages(
  messages: ClientThreadMessage[] | null | undefined,
): ClientThreadMessage[] {
  const rows = Array.isArray(messages) ? messages : []
  const clean = rows.filter((m) => !(m && Object.prototype.hasOwnProperty.call(m, 'is_internal_note')))
  const dropped = rows.length - clean.length
  if (dropped > 0) {
    lastLeak.value = { count: dropped, at: new Date().toISOString() }
    // Loud on purpose. A silent filter would repair the symptom and hide a
    // live privacy defect for as long as nobody happened to read the SQL.
    console.error(
      `[client-messages] P0: ${dropped} message(s) arrived carrying is_internal_note. ` +
        'The server must never send that field to a client. Dropped locally; report this.',
    )
  }
  return clean
}

function sanitizeThread(thread: ClientThread): ClientThread {
  return { ...thread, messages: sanitizeClientMessages(thread.messages) }
}

export const clientMessagesService = {
  /** The caller's own company thread. Reading never creates rows. */
  getThread: () =>
    api.get<ClientThread>('/v1/client/messages').then((r) => sanitizeThread(r.data)),

  /** Post into the caller's own thread; mints the thread on the first call. */
  post: (body: string) =>
    api
      .post<ClientThreadMessage>('/v1/client/messages', { body })
      .then((r) => sanitizeClientMessages([r.data])[0] ?? null),

  /** Mark the consultant's messages read. */
  markRead: () =>
    api.post<ClientReadResult>('/v1/client/messages/read').then((r) => r.data),

  /**
   * Presence ping. Returns the unread badge in the same round trip by design —
   * one poll answers both "am I still here?" and "is there anything new?".
   */
  heartbeat: () =>
    api.post<ClientHeartbeatResult>('/v1/client/messages/heartbeat').then((r) => r.data),
}
